import type { Cadence } from '@act/shared/data-transfer-objects';
import { useEffect } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';

/** Returns true while a useApiCall request has not finished its first fetch. */
const isApiRequestPending = (loading: boolean | null): boolean =>
  loading !== false;

interface UseReportMetadataPeriodLoadersParams {
  customerId: string;
  cadence: Cadence;
  mode: 'create' | 'edit';
  measurementPeriod: string;
  endMonthKey: string;
  setMeasurementPeriod: (value: string) => void;
  setEndMonthKey: (value: string) => void;
}

/**
 * Loads warehouse period options and applies create-mode defaults.
 * @param params - Customer, cadence, and period field state.
 * @returns API option lists and loading flags.
 */
export const useReportMetadataPeriodLoaders = ({
  customerId,
  cadence,
  mode,
  measurementPeriod,
  endMonthKey,
  setMeasurementPeriod,
  setEndMonthKey,
}: UseReportMetadataPeriodLoadersParams) => {
  const [
    measurementPeriodOptions,
    measurementPeriodsLoading,
    loadMeasurementPeriods,
  ] = useApiCall(reportsRequests.listCustomerMeasurementPeriods);
  const [reportingMonthOptions, reportingMonthsLoading, loadReportingMonths] =
    useApiCall(reportsRequests.listCustomerReportingMonths);

  useEffect(() => {
    if (!customerId) {
      return;
    }
    loadMeasurementPeriods({ params: { id: customerId } });
    if (cadence !== 'adhoc') {
      loadReportingMonths({ params: { id: customerId } });
    }
  }, [cadence, customerId, loadMeasurementPeriods, loadReportingMonths]);

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }
    setMeasurementPeriod('');
    setEndMonthKey('');
  }, [customerId, mode, setEndMonthKey, setMeasurementPeriod]);

  useEffect(() => {
    if (mode !== 'create' || cadence === 'adhoc') {
      return;
    }
    const options = reportingMonthOptions ?? [];
    if (options.length > 0 && !endMonthKey) {
      setEndMonthKey(options[0]?.value ?? '');
    }
  }, [cadence, endMonthKey, mode, reportingMonthOptions, setEndMonthKey]);

  useEffect(() => {
    if (mode !== 'create' || cadence !== 'adhoc') {
      return;
    }
    const options = measurementPeriodOptions ?? [];
    if (options.length > 0 && !measurementPeriod) {
      setMeasurementPeriod(options[0]?.value ?? '');
    }
  }, [
    cadence,
    measurementPeriod,
    measurementPeriodOptions,
    mode,
    setMeasurementPeriod,
  ]);

  const periodOptionsLoading =
    cadence === 'adhoc'
      ? isApiRequestPending(measurementPeriodsLoading)
      : isApiRequestPending(measurementPeriodsLoading) ||
        isApiRequestPending(reportingMonthsLoading);

  return {
    measurementPeriodOptions: measurementPeriodOptions ?? [],
    reportingMonthOptions: reportingMonthOptions ?? [],
    measurementPeriodsLoading,
    reportingMonthsLoading,
    periodOptionsLoading,
    loadMeasurementPeriods,
    loadReportingMonths,
  };
};
